/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Flex,
  Form,
  Input,
  Select,
} from 'antd';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useState } from 'react';
import tw from 'twin.macro';

import { useAllBranches } from '@/hooks/queries/admin';
import useBelowWidth from '@/hooks/useBelowWidth';
import type usePagination from '@/hooks/usePagination';

type AdditionalParams = Record<string, unknown>;

type CommonConfig = {
  label: ReactNode;
  placeholder?: string;
};

type SingleValue<T extends AdditionalParams> = {
  name: keyof T;
};

type TwoValue<T extends AdditionalParams> = {
  names: [keyof T, keyof T];
};

type FilterConfigItem<T extends AdditionalParams> =
  | ({ type: 'input' } & CommonConfig & SingleValue<T>)
  | ({
      type: 'select';
      options: Record<string | number, string>;
      format?: (value: any) => boolean | string | number;
    } & CommonConfig &
      SingleValue<T>)
  | ({
      type: 'branch';
    } & CommonConfig &
      SingleValue<T>)
  | ({ type: 'date' } & CommonConfig & SingleValue<T>)
  | ({ type: 'dateRange' } & CommonConfig & TwoValue<T>);

interface Props<T extends AdditionalParams> {
  pagination: ReturnType<typeof usePagination<T>>;
  filters: FilterConfigItem<T>[];
}

const Filter = <T extends AdditionalParams>({
  pagination,
  filters,
}: Props<T>) => {
  const max768 = useBelowWidth(768);
  const [inputs, setInputs] = useState<Partial<Record<keyof T, unknown>>>({});
  const names = filters.flatMap((filter) =>
    'name' in filter ? filter.name : filter.names
  );
  const getAllBranches = useAllBranches({
    enabled: filters.some((filter) => filter.type === 'branch'),
  });

  return (
    <Card css={tw`bg-[#f5f5f5]`}>
      <Form
        onFinish={() => {
          const newInputs = { ...inputs };
          const invalidKeys = Object.keys(inputs).filter(
            (key) => !names.includes(key)
          );

          invalidKeys.forEach((invalidKey) => {
            delete newInputs[invalidKey];
          });

          setInputs(newInputs);

          const resetParams = invalidKeys.reduce<Record<string, string>>(
            (acc, key) => {
              acc[key] = '';
              return acc;
            },
            {}
          );

          pagination.setPaginationValues({
            ...inputs,
            ...resetParams,
          } as any);
        }}
      >
        <Descriptions
          column={max768 ? 1 : 2}
          styles={{
            root: {
              width: filters.length === 1 ? '50%' : '100%',
            },
            label: {
              width: 80,
              justifyContent: 'flex-end',
              alignItems: 'center',
              color: 'rgba(0, 0, 0, 0.88)',
            },
          }}
        >
          {filters.map((item) => (
            <Descriptions.Item key={item.label as string} label={item.label}>
              {(() => {
                switch (item.type) {
                  case 'input':
                    return (
                      <Input
                        width="100%"
                        value={(inputs[item.name] as string) || ''}
                        placeholder={item.placeholder}
                        onChange={(e) => {
                          setInputs({
                            ...inputs,
                            [item.name]: e.target.value as any,
                          });
                        }}
                      />
                    );
                  case 'date':
                    return (
                      <DatePicker
                        style={{ width: '100%' }}
                        picker="date"
                        placeholder={item.placeholder || ''}
                        value={
                          pagination.query[item.name]
                            ? dayjs(pagination.query[item.name] as any)
                            : null
                        }
                        onChange={(_, value) => {
                          pagination.setPaginationValue(
                            item.name,
                            value as any
                          );
                        }}
                      />
                    );
                  case 'dateRange': {
                    const [startName, endName] = item.names;

                    return (
                      <DatePicker.RangePicker
                        style={{ width: '100%' }}
                        picker="date"
                        placeholder={[
                          item?.placeholder || '',
                          item?.placeholder || '',
                        ]}
                        value={[
                          pagination.query[startName]
                            ? dayjs(pagination.query[startName] as any)
                            : null,
                          pagination.query[endName]
                            ? dayjs(pagination.query[endName] as any)
                            : null,
                        ]}
                        onChange={(_, [startDate, endDate]) => {
                          pagination.setPaginationValues({
                            [startName]: startDate,
                            [endName]: endDate,
                          } as any);
                        }}
                        presets={[
                          {
                            label: '1주일',
                            value: [dayjs().subtract(1, 'week'), dayjs()],
                          },
                          {
                            label: '1개월',
                            value: [dayjs().subtract(1, 'month'), dayjs()],
                          },
                          {
                            label: '3개월',
                            value: [dayjs().subtract(3, 'month'), dayjs()],
                          },
                          {
                            label: '6개월',
                            value: [dayjs().subtract(6, 'month'), dayjs()],
                          },
                        ]}
                      />
                    );
                  }
                  case 'select':
                    return (
                      <Select
                        style={{ width: '100%' }}
                        placeholder={item.placeholder}
                        value={pagination.query[item.name]}
                        onChange={(value) =>
                          pagination.setPaginationValue(item.name, value as any)
                        }
                      >
                        {[[null, '전체']]
                          .concat(Object.entries(item.options))
                          .map(([value, label]) => (
                            <Select.Option
                              key={value}
                              value={
                                item.format && value
                                  ? item.format(value)
                                  : value
                              }
                            >
                              {label}
                            </Select.Option>
                          ))}
                      </Select>
                    );
                  case 'branch':
                    return (
                      <Select
                        style={{ width: '100%' }}
                        placeholder={item.placeholder}
                        value={pagination.query[item.name]}
                        onChange={(value) =>
                          pagination.setPaginationValue(item.name, value as any)
                        }
                        showSearch
                        filterOption={(input, option) =>
                          option!.label
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        options={[{ label: '전체', value: null }].concat(
                          (getAllBranches.data ?? []).map(
                            ({ id, branchName }) => ({
                              label: branchName,
                              value: id,
                            })
                          )
                        )}
                      />
                    );
                  default:
                    return '-';
                }
              })()}
            </Descriptions.Item>
          ))}
        </Descriptions>

        <Flex css={tw`mt-[24px]`} justify="center" align="center" gap={8}>
          <Button
            htmlType="button"
            onClick={() => {
              setInputs({});
              pagination.reset();
            }}
          >
            초기화
          </Button>

          <Button type="primary" htmlType="submit">
            검색
          </Button>
        </Flex>
      </Form>
    </Card>
  );
};

export default Filter;
